import React from "react";
import styled from "styled-components";
import { css } from "styled-components";
import { breakpoints, colors, fontFamily, sectionRhythm } from "../../../theme";

const isImageSrc = (src) =>
  typeof src === "string" &&
  (/\/image\/upload\//.test(src) ||
    /images\.unsplash\.com\//.test(src) ||
    /\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(src));

/** Matches homepage project-card cover ratio (1536×1024). */
export const HERO_IMAGE_WIDTH = 1536;
export const HERO_IMAGE_HEIGHT = 1024;

export const HeroImage = styled.img`
  width: 100%;
  height: auto;
  aspect-ratio: ${HERO_IMAGE_WIDTH} / ${HERO_IMAGE_HEIGHT};
  display: block;
  object-fit: cover;
  border-radius: 6px;

  @media only screen and (min-width: ${breakpoints.tablet}) {
    border-radius: 12px;
  }
`;

const MediaUrlText = styled.div`
  margin: 1.5rem 0 0.5rem;
  padding: 0.75rem 1rem;
  font-size: 12px;
  line-height: 1.4;
  word-break: break-all;
  color: ${colors.grey.darkMedium};
  background: ${colors.surface};
  border: 1px solid ${colors.form.border};
  border-radius: 8px;
  user-select: all;
`;

export function ProjectHero({ src, alt = "" }) {
  if (!src) return null;
  if (isImageSrc(src)) {
    return (
      <HeroImage
        src={src}
        alt={alt}
        width={HERO_IMAGE_WIDTH}
        height={HERO_IMAGE_HEIGHT}
        decoding="async"
      />
    );
  }
  return <MediaUrlText>{src}</MediaUrlText>;
}

export const caseStudyBodyStyles = css`
  display: flex;
  flex-direction: column;
  gap: ${sectionRhythm.paddingY.mobile}px;
  margin-top: ${sectionRhythm.paddingY.mobile}px;
  margin-bottom: ${sectionRhythm.paddingY.mobile}px;

  @media only screen and (min-width: ${sectionRhythm.breakpoint}) {
    gap: ${sectionRhythm.paddingY.desktop}px;
    margin-top: ${sectionRhythm.paddingY.desktop}px;
    margin-bottom: ${sectionRhythm.paddingY.desktop}px;
  }

  p,
  q {
    letter-spacing: -0.003em;
    line-height: 28px;
    font-size: 16px;
    color: ${colors.body};
    margin: 0;

    @media only screen and (min-width: ${breakpoints.tablet}) {
      font-size: 20px;
      line-height: 32px;
    }
  }

  p + p {
    margin-top: 1em;
  }

  h4 {
    font-family: ${fontFamily.extraBold};
    margin-top: ${sectionRhythm.paddingY.mobile}px;
    margin-bottom: 8px;
    font-size: 24px;
    letter-spacing: 0;
    line-height: 1.2;

    @media only screen and (min-width: ${breakpoints.tablet}) {
      font-size: 28px;
    }

    @media only screen and (min-width: ${sectionRhythm.breakpoint}) {
      margin-top: ${sectionRhythm.paddingY.desktop}px;
    }
  }

  h4:has(+ ul),
  h4:has(+ ol) {
    margin-bottom: 8px;

    @media only screen and (min-width: ${breakpoints.tablet}) {
      margin-bottom: 12px;
    }
  }

  h5 {
    font-family: ${fontFamily.extraBold};
    margin: 1.5em 0 8px;
    font-size: 16px;
    letter-spacing: 0;
    line-height: 1.3;
    color: ${colors.body};

    @media only screen and (min-width: ${breakpoints.tablet}) {
      font-size: 20px;
    }
  }

  /* Subheading right under a section title should stay tight */
  h4 + h5 {
    margin-top: 4px;
  }

  h4 + [data-testimonials] {
    margin-top: 8px;
  }

  h4:has(+ [data-testimonials]) {
    margin-bottom: 0;
  }

  /* Gap on the body handles section spacing; don't add section margins too */
  section {
    margin: 0;
    padding: 0;

    & > :first-child {
      margin-top: 0;
    }
  }

  ol,
  ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  h4 + ul,
  h4 + ol,
  h5 + ul,
  h5 + ol {
    margin-top: 0;
  }

  p + ul,
  p + ol {
    margin-top: 0.75em;
  }

  ul + h5,
  ol + h5,
  p + h5 {
    margin-top: 1.5em;
  }

  li {
    margin-top: 0;
    letter-spacing: -0.003em;
    line-height: 28px;
    margin-left: 20px;
    font-size: 16px;
    color: ${colors.body};

    @media only screen and (min-width: ${breakpoints.tablet}) {
      font-size: 20px;
      line-height: 32px;
    }

    &:first-child {
      margin-top: 0;
    }
  }

  ul > li {
    list-style-type: disc;
  }

  ol > li {
    list-style-type: decimal;
  }
`;
